import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import useWebSocket from '../hooks/useWebSocket';

const RealtimeContext = createContext(null);

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

export const RealtimeProvider = ({ children }) => {
  const [extensionStatus, setExtensionStatus] = useState({});
  const [lastUpdate, setLastUpdate] = useState(null);
  const [statusChanges, setStatusChanges] = useState([]);

  const handleMessage = useCallback((data) => {
    if (data.type === 'status_update' && data.changes) {
      // Update extension status
      const newStatus = { ...extensionStatus };
      
      data.changes.forEach(change => {
        newStatus[change.extension_id] = {
          status: change.new_status,
          phone_status: change.new_phone_status,
          ip_address: change.ip_address,
          name: change.name,
          timestamp: change.timestamp
        };
      });
      
      setExtensionStatus(newStatus);
      setLastUpdate(data.timestamp);
      
      // Add to status changes history (keep last 10)
      setStatusChanges(prev => {
        const newChanges = [...data.changes, ...prev].slice(0, 10);
        return newChanges;
      });
    } else if (data.type === 'connected') {
      console.log('Real-time connection established');
    }
  }, [extensionStatus]);

  const { isConnected, lastMessage, sendMessage } = useWebSocket(handleMessage);

  // Function to get extension status
  const getExtensionStatus = useCallback((extensionId) => {
    return extensionStatus[extensionId] || null;
  }, [extensionStatus]);

  // Subscribe to status updates for specific extension
  const subscribeToExtension = useCallback((extensionId, callback) => {
    // This would be used for more targeted subscriptions
    // For now, components can use the extensionStatus state directly
  }, []);

  return (
    <RealtimeContext.Provider value={{
      isConnected,
      extensionStatus,
      lastUpdate,
      statusChanges,
      getExtensionStatus,
      subscribeToExtension,
      sendMessage
    }}>
      {children}
    </RealtimeContext.Provider>
  );
};

export default RealtimeContext;
